/* Prices: what you charge, for every type of every service you offer.
 *
 * The owner's ruling (2026-10-23): when a family books, it sees the nearby
 * agencies that can serve it, each with ITS OWN price, cheapest first, and
 * picks one. So a type you have no price for is a type families never see you
 * for — the screen says so in those words, beside every row that has none.
 * Independent workers charge the platform's price; it is shown beside yours
 * for reference ("independents charge ₹X"), never as a limit.
 *
 * Saving a price puts it in force at once, for new bookings (PUT
 * /agency/prices); no one at the platform approves it, though the platform's
 * team can read it. "Stop offering" closes the price (DELETE) — the record is
 * kept. An ambulance price is a base fare that covers some kilometres, and a
 * rate per kilometre beyond.
 *
 * Owners and admins change prices; a dispatcher or accountant reads them (the
 * server refuses the write anyway — hiding the button is not a permission). */

import { useMemo, useState } from 'react';
import { clearPrice, getPrices, savePrice, type AgencyPriceItem } from '../data/agency';
import { dateTime, money } from '../data/format';
import { serviceName } from '../data/words';
import { canManage } from '../nav';
import { useProfile } from '../shell/profile';
import { ActionWithReason, Btn, Card, CardHead, CellStack, Empty, Field, Input, Kpi, KpiGrid, Note, Stack, Table, TRow, uiCss } from '../ui';
import { ServiceDot, useAsync, useNotice } from './common';
import { PriceSheetCard } from './priceSheet';

const COLS = '1.5fr 1.3fr 1.1fr 1.3fr 1.2fr';

type Focus = 'all' | 'unset' | 'set';

/** The amount as the person typed it, checked the way the server checks it:
 *  above zero (or zero when allowed), at most two decimals. null = not valid. */
function amountOf(v: string, zeroOK = false): string | null {
  const s = v.trim().replace(/,/g, '').replace(/^₹/, '');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  if (!zeroOK && Number(s) === 0) return null;
  return s;
}

function priceText(it: AgencyPriceItem): string {
  if (!it.price) return '';
  if (it.service_code === 'AMBULANCE') {
    const inc = Number(it.price.included_km ?? '0');
    const km = it.price.per_km_amount ? `, then ${money(it.price.per_km_amount)} a km` : '';
    return `${money(it.price.base_amount)} for the first ${inc} km${km}`;
  }
  return money(it.price.base_amount);
}

function platformText(it: AgencyPriceItem): string {
  if (!it.platform_price) return 'No platform price here';
  if (it.service_code === 'AMBULANCE' && it.platform_included_km) {
    const km = it.platform_per_km_amount ? `, then ${money(it.platform_per_km_amount)} a km` : '';
    return `Independents charge ${money(it.platform_price)} for ${Number(it.platform_included_km)} km${km}`;
  }
  return `Independents charge ${money(it.platform_price)}`;
}

export function PricesScreen() {
  const { profile } = useProfile();
  const manage = canManage(profile.me.role);
  const prices = useAsync(getPrices, []);
  const { notice, run } = useNotice();
  const [focus, setFocus] = useState<Focus>('all');
  const [editing, setEditing] = useState<AgencyPriceItem | null>(null);
  const [stopping, setStopping] = useState<AgencyPriceItem | null>(null);
  const [base, setBase] = useState('');
  const [incKm, setIncKm] = useState('');
  const [perKm, setPerKm] = useState('');

  const items = prices.data?.items ?? [];
  const unset = items.filter((i) => !i.price);
  const shown = useMemo(
    () => items.filter((i) => (focus === 'unset' ? !i.price : focus === 'set' ? !!i.price : true)),
    [items, focus],
  );
  const lit = (f: Focus) => (focus === f ? { bg: 'var(--teal-bg)', bd: 'var(--teal)', c: 'var(--teal)' } : undefined);
  const go = (f: Focus) => setFocus((cur) => (cur === f ? 'all' : f));

  const openEdit = (it: AgencyPriceItem) => {
    setEditing(it);
    // Start from the agency's own price, or else the platform's as a guide.
    setBase(it.price?.base_amount ?? it.platform_price ?? '');
    setIncKm(it.price?.included_km ?? it.platform_included_km ?? '10');
    setPerKm(it.price?.per_km_amount ?? it.platform_per_km_amount ?? '');
  };

  const isAmb = editing?.service_code === 'AMBULANCE';
  const blocked = !editing
    ? null
    : !amountOf(base)
      ? 'Type the price — a number above zero, with at most two decimals.'
      : isAmb && !amountOf(incKm, true)
        ? 'Type how many kilometres the base fare covers (0 if none).'
        : isAmb && !amountOf(perKm)
          ? 'Type the rate for each kilometre beyond.'
          : null;

  const label = (it: AgencyPriceItem) => `${it.service_name || serviceName(it.service_code)} — ${it.variant_name}`;

  return (
    <Stack>
      {notice}
      {prices.error ? <Note tone="danger">Your prices could not be read: {prices.error}</Note> : null}
      {!manage ? <Note tone="amber">Only an owner or admin can change prices. You can read them.</Note> : null}
      {prices.data && items.length === 0 ? (
        <Note tone="amber">You offer no services yet. Switch them on in Services &amp; area, then set a price for each.</Note>
      ) : null}

      <KpiGrid>
        <Kpi
          label="No price set"
          value={prices.data ? unset.length : '—'}
          note={unset.length ? "families won't see you for these" : 'every type you offer has a price'}
          color={unset.length ? 'var(--amber)' : undefined}
          onClick={() => go('unset')}
          tone={lit('unset')}
        />
        <Kpi
          label="Priced"
          value={prices.data ? items.length - unset.length : '—'}
          note="families can pick you for these"
          onClick={() => go('set')}
          tone={lit('set')}
        />
        <Kpi label="Types you offer" value={prices.data ? items.length : '—'} note="every type of every service switched on" onClick={() => go('all')} tone={lit('all')} />
      </KpiGrid>

      {items.length > 0 ? <PriceSheetCard manage={manage} onApplied={prices.reload} /> : null}

      <Card>
        <CardHead
          title="Your prices"
          sub="A family sees your price beside the others near them and picks one. A new price counts for new bookings from the moment you save it."
          right={focus !== 'all' ? <Btn size="sm" onClick={() => setFocus('all')}>Show all</Btn> : undefined}
        />
        {prices.loading && !prices.data ? (
          <Empty>Reading your prices…</Empty>
        ) : shown.length === 0 ? (
          <Empty>{focus === 'unset' ? 'Every type you offer has a price.' : 'Nothing to show.'}</Empty>
        ) : (
          <Table cols={COLS} head={['Service', 'Your price', 'Charged', 'For reference', '']} minWidth={880}>
            {shown.map((it) => (
              <TRow key={`${it.service_code}:${it.variant_code}`} cols={COLS}>
                <CellStack
                  top={
                    <span>
                      <ServiceDot code={it.service_code} />
                      {it.variant_name}
                    </span>
                  }
                  sub={it.service_name || serviceName(it.service_code)}
                />
                {it.price ? (
                  <CellStack top={priceText(it)} sub={it.price.updated_at ? `since ${dateTime(it.price.updated_at)}` : undefined} />
                ) : (
                  <CellStack top={<span style={{ color: 'var(--amber)' }}>Not set</span>} sub="Families won't see you for this" />
                )}
                <span className={uiCss.sub}>{it.unit_label}</span>
                <span className={uiCss.sub}>{platformText(it)}</span>
                <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {manage ? (
                    <Btn size="xs" variant={it.price ? undefined : 'primary'} onClick={() => openEdit(it)}>
                      {it.price ? 'Change' : 'Set a price'}
                    </Btn>
                  ) : null}
                  {manage && it.price ? (
                    <Btn size="xs" variant="danger" onClick={() => setStopping(it)}>
                      Stop offering
                    </Btn>
                  ) : null}
                </span>
              </TRow>
            ))}
          </Table>
        )}
      </Card>

      <ActionWithReason
        open={editing !== null}
        onClose={() => setEditing(null)}
        kicker={editing?.price ? 'Change a price' : 'Set a price'}
        title={editing ? label(editing) : ''}
        desc={
          editing
            ? `${editing.unit_label[0]?.toUpperCase() ?? ''}${editing.unit_label.slice(1)}. ${platformText(editing)}.`
            : ''
        }
        codes={[]}
        fields={
          editing ? (
            <>
              <Field label={isAmb ? 'Base fare (₹)' : `Your price (₹, ${editing.unit_label})`}>
                <Input value={base} onChange={setBase} placeholder="1400" ariaLabel="Price in rupees" />
              </Field>
              {isAmb ? (
                <>
                  <Field label="Kilometres the base fare covers">
                    <Input value={incKm} onChange={setIncKm} placeholder="10" ariaLabel="Kilometres included" />
                  </Field>
                  <Field label="Each kilometre beyond (₹)">
                    <Input value={perKm} onChange={setPerKm} placeholder="25" ariaLabel="Rupees per kilometre beyond" />
                  </Field>
                </>
              ) : null}
            </>
          ) : null
        }
        blocked={blocked}
        impact="Families see this price from the moment you save it, for new bookings. Bookings already made keep the price they were made at."
        okLabel="Save price"
        onConfirm={() => {
          const it = editing;
          setEditing(null);
          if (!it) return;
          const body = {
            service_code: it.service_code,
            variant_code: it.variant_code,
            base_amount: amountOf(base) ?? base,
            ...(it.service_code === 'AMBULANCE' ? { included_km: amountOf(incKm, true) ?? incKm, per_km_amount: amountOf(perKm) ?? perKm } : {}),
          };
          void run(() => savePrice(body), `${label(it)}: your price is saved and families see it now.`).then(() => prices.reload());
        }}
      />

      <ActionWithReason
        open={stopping !== null}
        onClose={() => setStopping(null)}
        kicker="Stop offering"
        title={stopping ? `Stop offering ${label(stopping)}?` : ''}
        desc="Your price is taken down. Families will not see you for this until you set a price again. Jobs you already have are not affected."
        codes={[]}
        okLabel="Stop offering"
        okTone="danger"
        onConfirm={() => {
          const it = stopping;
          setStopping(null);
          if (!it) return;
          void run(() => clearPrice(it.service_code, it.variant_code), `${label(it)}: families no longer see you for this.`).then(() => prices.reload());
        }}
      />
    </Stack>
  );
}
